import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { INQUIRY_PROCESS_NAME, resolveLatestProcessName } from '../../transactions/transaction';

import { Heading, Modal } from '../../components';
import UserCard from './UserCard/UserCard';
import InquiryForm from './InquiryForm/InquiryForm';

import css from './ListingPage.module.css';

const CONTACT_USER_LINK = 'inquiryModalContactUserLink';

const SectionAuthorMaybe = props => {
  const {
    title,
    listing,
    authorDisplayName,
    onContactUser,
    isInquiryModalOpen,
    onCloseInquiryModal,
    sendInquiryError,
    sendInquiryInProgress,
    onSubmitInquiry,
    currentUser,
    onManageDisableScrolling,
    config,
    hideContactActions = false,
    showContactInSidebar = false,
    specialistId = null,
  } = props;

  const intl = useIntl();

  if (!listing.author) {
    return null;
  }

  // D19: the author of a task is a client, the author of any other listing type is a specialist.
  // OrderPanel's sidebar contact button uses the same listingType condition.
  const isTaskListing = listing.attributes?.publicData?.listingType === 'tasks';
  const contactMessageId = isTaskListing ? 'UserCard.contactClient' : 'UserCard.contactSpecialist';
  // F3: the «log in as a specialist» notice is not shown to the author of the task
  // (same own-listing expression as ListingPageCarousel / ListingPageCoverPhoto).
  const isOwnListing = !!(
    currentUser &&
    listing.author &&
    listing.author.id.uuid === currentUser.id?.uuid
  );

  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isInquiryProcess = processName === INQUIRY_PROCESS_NAME;
  // Hide the lower contact action only when the sidebar already exposes it,
  // so there is a single contact entry point. "View profile" stays.
  const showContact = !isInquiryProcess && !hideContactActions && !showContactInSidebar;
  // The sidebar contact lives in a desktop-only card, so below --viewportLarge
  // it is not visible. Restore a mobile/tablet-only contact action here so
  // non-owner viewers keep a visible way to reach the provider. Shown only when
  // the sidebar would otherwise be the single (desktop) contact entry point.
  const showMobileContact = showContactInSidebar && !isInquiryProcess && !hideContactActions;

  return (
    <section id="author" className={css.sectionAuthor}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage
          id={isTaskListing ? 'ListingPage.aboutClientTitle' : 'ListingPage.aboutSpecialistTitle'}
        />
      </Heading>
      <UserCard
        user={listing.author}
        currentUser={currentUser}
        onContactUser={onContactUser}
        showContact={showContact}
        contactLinkId={CONTACT_USER_LINK}
        contactMessageId={contactMessageId}
      />
      {/* A12: the specialist ID appears once, here, and only for services (a task's author
          acts as a client). */}
      {specialistId && !isTaskListing ? (
        <div className={css.providerSpecialistMeta}>
          {intl.formatMessage({ id: 'ListingPage.specialistIdLabel' }, { id: specialistId })}
        </div>
      ) : null}
      {showMobileContact ? (
        <button
          type="button"
          id="mobileAuthorContactLink"
          className={css.mobileContactAction}
          onClick={() => onContactUser?.(listing.author)}
        >
          <FormattedMessage id={contactMessageId} />
        </button>
      ) : null}
      {hideContactActions && !isOwnListing ? (
        <div className={css.customerRestrictionNotice}>
          <FormattedMessage
            id="ListingPage.loginAsSpecialistToApply"
            defaultMessage="Чтобы откликнуться на задачу, войдите как специалист"
          />
        </div>
      ) : null}
      <Modal
        id="ListingPage.inquiry"
        contentClassName={css.inquiryModalContent}
        isOpen={isInquiryModalOpen}
        onClose={onCloseInquiryModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={CONTACT_USER_LINK}
      >
        <InquiryForm
          className={css.inquiryForm}
          submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
          listingTitle={title}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
          headingMessageId={isTaskListing ? 'InquiryForm.headingTask' : 'InquiryForm.heading'}
          config={config}
        />
      </Modal>
    </section>
  );
};

export default SectionAuthorMaybe;
